import { useEffect, useRef } from 'react';
import {
  browserOffline,
  cacheRows,
  cachedRows,
  pendingOps,
  shadowUpdate,
  type Request,
  type UseOutbox,
} from '@dia/db';
import { supabase } from '../../supabase';
import { composeMaintenanceText, type Lang, type RequestTarget } from './text';
import { translateText } from './data';
import { owedSpanish, withQueuedUpdates } from './requestWork';

/**
 * Sending a maintenance report. The row goes into the outbox at once, in the
 * reporter's own language plus the structured half in both. Only the free note
 * needs the translate function, and that happens afterwards: if it works, a
 * second queued write fills in the other language, and if the phone is offline
 * it is tried again when the signal returns. Nothing about sending waits for it.
 */

export type SendRequestInput = {
  enqueue: UseOutbox['enqueue'];
  requestId: string;
  staffId: string | null;
  token: string;
  lang: Lang;
  target: RequestTarget;
  note: string;
  assetId: string | null;
  roomId: string | null;
  /** Entries as stored in photo_paths, see bucketAndPath in text.ts. */
  photoPaths: string[];
  voiceNotePath: string | null;
  priority: number;
};

export async function sendMaintenanceRequest(input: SendRequestInput): Promise<void> {
  const note = input.note.trim();
  const base = composeMaintenanceText(input.target, { text: note, lang: input.lang, translated: null });

  await input.enqueue({
    id: input.requestId,
    table: 'requests',
    payload: {
      id: input.requestId,
      type: 'maintenance',
      asset_id: input.assetId,
      room_id: input.roomId,
      created_by_guest: false,
      created_by_staff_id: input.staffId,
      text_original: base.text_original,
      lang_original: base.lang_original,
      text_es: base.text_es,
      text_en: base.text_en,
      status: 'new',
      priority: input.priority,
      photo_paths: input.photoPaths,
      voice_note_path: input.voiceNotePath,
    },
  });

  if (!note) return;

  const other: Lang = input.lang === 'es' ? 'en' : 'es';
  const fill = async (): Promise<boolean> => {
    const result = await translateText(note, input.lang, other, input.token);
    if (!result.ok) return false;
    const full = composeMaintenanceText(input.target, { text: note, lang: input.lang, translated: result.text });
    await input.enqueue({
      table: 'requests',
      kind: 'update',
      payload: { text_es: full.text_es, text_en: full.text_en },
      match: { client_id: input.requestId },
    });
    return true;
  };

  void fill().then((ok) => {
    if (ok || typeof window === 'undefined') return;
    // Offline or a bad moment: try once more when the signal is back.
    window.addEventListener('online', () => void fill(), { once: true });
  });
}

/**
 * One request, whatever its status. The open queue drops a request the moment
 * it is done, so the request screen reads its own row: tapping "Ya está listo"
 * must not turn the screen into "I cannot find this request". Network first,
 * the phone's copy second, and any tap still waiting laid over the top.
 */
export async function loadRequest(id: string): Promise<Request | null> {
  const entity = `request:${id}`;
  let row: Request | null = null;
  if (!browserOffline()) {
    try {
      const { data, error } = await supabase.from('requests').select('*').eq('id', id);
      if (!error && Array.isArray(data)) row = (await cacheRows(entity, data as Request[]))[0] ?? null;
    } catch {
      // Offline is normal here. Fall through to what the phone already has.
    }
  }
  if (!row) row = (await cachedRows<Request>(entity))[0] ?? null;
  if (!row) row = (await cachedRows<Request>('requests')).find((r) => r.id === id) ?? null;
  return row ? withQueuedUpdates(row, await pendingOps()) : null;
}

/**
 * The queue's half of the late translation (migration 022). A request that
 * reached the island in English with no Spanish gets it the first time
 * somebody on the queue looks with signal: translated once, queued through the
 * outbox, so it lands for every phone and not only this one. The guest's own
 * phone does the same from its side; whichever lands first stays.
 *
 * A few at a time, each tried once per visit unless it simply failed.
 */
export function useLateSpanish(requests: readonly Request[] | null, token: string, enqueue: UseOutbox['enqueue']): void {
  const tried = useRef<Set<string>>(new Set());
  const busy = useRef(false);
  useEffect(() => {
    if (!requests || requests.length === 0 || busy.current || browserOffline()) return;
    busy.current = true;
    void (async () => {
      try {
        const owed = owedSpanish(requests, await pendingOps())
          .filter((r) => !tried.current.has(r.client_id))
          .slice(0, 5);
        for (const r of owed) {
          tried.current.add(r.client_id);
          const result = await translateText(r.text_original, 'en', 'es', token, 'staff', 8000);
          if (!result.ok) {
            if (result.reason !== 'quota' && result.reason !== 'session') tried.current.delete(r.client_id);
            if (result.reason === 'offline') return;
            continue;
          }
          await enqueue(shadowUpdate('requests', { clientId: r.client_id, lang: 'es', text: result.text }));
        }
      } finally {
        busy.current = false;
      }
    })();
  }, [requests, token, enqueue]);
}
